import { useEffect, useRef, useState } from 'react'
import type { EmbeddedSession } from '../hooks/useBilling'

// Stripe Checkout's embedded form (ui_mode embedded_page), mounted inside the
// subscribe sheet. Stripe.js is loaded only here, on demand — the rest of the
// app never touches it. What the form looks like inside its frame is the Stripe
// Dashboard's Branding settings (docs/OPERATIONS.md › Stripe billing); around
// it, the sheet is ours. Managed Payments keeps Stripe the merchant of record:
// the form collects the address and shows tax, wallets and promotion codes.

export function EmbeddedCheckout({ session, onComplete }: {
  session: EmbeddedSession
  /** Paid: cards and wallets finish here; a redirecting method returns to ?billing=success. */
  onComplete: () => void
}) {
  const host = useRef<HTMLDivElement>(null)
  const done = useRef(onComplete)
  useEffect(() => { done.current = onComplete }, [onComplete])
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')

  useEffect(() => {
    let cancelled = false
    let checkout: { mount(el: HTMLElement): void; destroy(): void } | null = null
    void (async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js')
        const stripe = await loadStripe(session.publishableKey)
        if (!stripe || cancelled) return
        checkout = await stripe.createEmbeddedCheckoutPage({
          clientSecret: session.clientSecret,
          onComplete: () => done.current(),
        })
        if (cancelled || !host.current) { checkout.destroy(); return }
        checkout.mount(host.current)
        setState('ready')
      } catch {
        if (!cancelled) setState('failed')
      }
    })()
    return () => {
      cancelled = true
      checkout?.destroy()
    }
  }, [session.clientSecret, session.publishableKey])

  return (
    <div className="ml-checkout">
      {state === 'loading' && <p className="ml-caption ml-checkout__status" role="status">Loading secure payment…</p>}
      {state === 'failed' && (
        <p className="ml-checkout__status ml-checkout__status--error" role="alert">
          The payment form didn't load. Check your connection and try again.
        </p>
      )}
      <div ref={host} className="ml-checkout__frame" data-testid="stripe-checkout" />
    </div>
  )
}
